import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from "@angular/core";
import { Icon } from "../utils/icon/icon";
import { SafeHtmlPipe } from "../../pipes/safe-html.pipe";
import { Router } from "@angular/router";
import { Modal } from "../utils/modal/modal";
import { Button } from "../utils/button/button";
import { TranslocoModule } from "@jsverse/transloco";
import { FavoriteStore } from "src/app/features/store/favorite-store";
import { TileTypeEnum } from "src/app/core/interfaces/tile-interface";
import { PianoAnalyticsService } from "src/app/core/services/piano-analytics";
import { TilesApiService } from "src/app/core/services/tiles/tiles-service";

@Component({
  selector: "app-tile",
  imports: [Icon, SafeHtmlPipe, Modal, Button, TranslocoModule],
  templateUrl: "./tile.html",
  styleUrls: ["./tile.scss"],
  host: {
    "[class]": '"tile-" +variant() + " bento-element-" + size()',
  },
})
export class Tile {
  id = input<string>();
  title = input.required<string>();
  icon = input<string>();
  text = input<string>();
  ctaText = input<string>();
  ctaLink = input<string>();
  textDisclaimer = input<string | undefined>();
  isRedirectionExternal = input<boolean>(false);
  image = input<string>();
  showImageOnMobile = input<boolean>(true);
  variant = input<
    "primary" | "secondary" | "tertiary" | "quaternary" | "quinary"
  >("primary");
  buttonVariant = computed(() => {
    switch (this.variant()) {
      case "primary":
        return "secondary";
      case "secondary":
        return "primary";
      case "tertiary":
        return "primary";
      case "quaternary":
        return "primary";
      case "quinary":
        return "primary";
    }
  });
  size = input<"small" | "medium" | "large" | null>();
  status = input<"Approved" | "Pending" | undefined>();

  private readonly router = inject(Router);
  private readonly favoriteStore = inject(FavoriteStore);
  private readonly piano = inject(PianoAnalyticsService);
  private readonly tileService = inject(TilesApiService);

  effectiveTextDisclaimer = computed(() => {
    const disclaimer = this.textDisclaimer();
    return this.tileService.hasMeaningfulTextContent(disclaimer) ? disclaimer : undefined;
  });

  isModalOpen = signal<boolean>(false);

  isFavorite = computed(() =>
    this.id() ? this.favoriteStore.isTileFavorite()(this.id()) : false
  );

  isFavoriteLoading = computed(() =>
    this.id() ? this.favoriteStore.isLoading() : false
  );

  openModal(): void {
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
  }

  goToLink(): void {
    window.open(this.ctaLink(), "_blank", "noopener,noreferrer");
    this.piano.sendEvent("tiles.selection", { tile_name: this.title() });
    this.closeModal();
  }

  navigateToCtaLink(): void {
    this.piano.sendEvent("tiles.selection", { tile_name: this.title() });
    this.router.navigate([this.ctaLink()]);
  }

  toggleFavorite(): void {
    if (this.isFavorite()) {
      this.removeFromFavorites();
    } else {
      this.addToFavorites();
    }
  }

  async addToFavorites(): Promise<void> {
    await this.favoriteStore.addTileToFavorites(
      this.id(),
      this.title(),
      this.text() || null,
      this.ctaLink() || "",
      this.textDisclaimer() || null,
      this.isRedirectionExternal(),
      TileTypeEnum.Other
    );
  }

  async removeFromFavorites(): Promise<void> {
    await this.favoriteStore.removeTileFromFavorites(this.id());
  }

  constructor() {
    effect(() => {
      document.body.style.overflow = this.isModalOpen() ? "hidden" : "";
    });
  }

  private hasMeaningfulTextContent(content?: string): boolean {
    if (!content) {
      return false;
    }

    const normalizedContent = content.replace(/&nbsp;/gi, " ").trim();
    if (!normalizedContent) {
      return false;
    }

    const parsedDocument = new DOMParser().parseFromString(
      normalizedContent,
      "text/html"
    );

    const extractedText = (parsedDocument.body.textContent || "")
      .replaceAll('\u00A0', " ")
      .trim();

    return extractedText.length > 0;
  }
}
